import React, { useState, useEffect, useMemo } from "react";
import { Modal, Button, Row, Col, Form, InputGroup } from "react-bootstrap";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import ImageResize from "quill-image-resize-module-react";
import ScratchPadSessionPopup from "./common/ScratchPadSessionPopup";
import MicButton from "./common/MicButton";
import { supabase } from "../supabaseClient";
import { saveDraft, getDraft, deleteDraft } from "./common/DraftHistory";
import ConfirmationPopup from "./common/ConfirmationPopup";

// Register image resize module with Quill
Quill.register("modules/imageResize", ImageResize);

const linkifyHtml = (html) => {
  if (!html) return '';
  const container = document.createElement('div');
  container.innerHTML = html;
  const processNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.nodeValue;
      const parts = text.split(/(https?:\/\/[^\s]+)/);
      if (parts.length === 1) return;
      const fragment = document.createDocumentFragment();
      parts.forEach((part) => {
        if (/^https?:\/\//.test(part)) {
          const a = document.createElement('a');
          a.href = part;
          a.target = '_blank';
          a.rel = 'noopener noreferrer';
          a.textContent = part;
          fragment.appendChild(a);
        } else {
          fragment.appendChild(document.createTextNode(part));
        }
      });
      node.replaceWith(fragment);
    } else if (node.nodeType === Node.ELEMENT_NODE && node.tagName.toLowerCase() !== 'a') {
      Array.from(node.childNodes).forEach(processNode);
    }
  };
  Array.from(container.childNodes).forEach(processNode);
  return container.innerHTML;
}
const quillTabBindings = {
  indentWithTab: {
    key: 9,
    shiftKey: false,
    handler(range) {
      this.quill.format("indent", "+1", Quill.sources.USER);
      this.quill.setSelection(range.index, range.length, Quill.sources.SILENT);
      return false;
    },
  },
  outdentWithShiftTab: {
    key: 9,
    shiftKey: true,
    handler(range) {
      this.quill.format("indent", "-1", Quill.sources.USER);
      this.quill.setSelection(range.index, range.length, Quill.sources.SILENT);
      return false;
    },
  },
};

function NotesModal({ show, onHide, isDarkMode, userId }) {
  const [notes, setNotes] = useState([]);
  const [selectedNoteId, setSelectedNoteId] = useState(null);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showEditor, setShowEditor] = useState(true);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [justCreated, setJustCreated] = useState(false); // Track if a new note was just created
  const [draftMode, setDraftMode] = useState(false); // Track if we're creating a new note (not yet saved)
  const [showQueueFormPopup, setShowQueueFormPopup] = useState(false);
  const [scratchPadId, setScratchPadId] = useState(false);
  const [scratchTitle, setScratchTitle] = useState(false);
  const [sideConvoList, setSideConvoList] = useState([]);

  // Add state for delete confirmation
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [apiToken, setApiToken] = useState([]);

  // Loading state for fetching scratchpads
  const [isLoading, setIsLoading] = useState(true);

  // Sidebar toggler state
  const [showScratchpadSidebar, setShowScratchpadSidebar] = useState(true);

  const zoomey_api_url = process.env.REACT_APP_ZOOMEY_API_URL;

  // Fetch notes from Supabase and sort by last_open desc
  useEffect(() => {
    if (!show) return;
    const fetchNotes = async () => {
      if (!userId) return;
      setIsLoading(true);
      const { data, error } = await supabase
        .from("scratch_pad")
        .select("*")
        .eq("user_id", userId)
        .order("last_open", { ascending: false });
      if (!error && data) {
        setNotes(data);
        // Set the active note (is_active) or the first note
        const active = data.find((n) => n.is_active);
        setSelectedNoteId(active ? active.id : data[0]?.id || null);
      }
      setIsLoading(false);
    };
    fetchNotes();
    fetchSessions();
  }, [show]);

  // Get selected note
  const selectedNote = notes.find((n) => n.id === selectedNoteId);

  // When selectedNoteId changes, update edit fields and set edit mode
  useEffect(() => {
    if (draftMode) return; // Don't auto-switch when in draft mode
    setEditTitle(selectedNote?.title || "");
    setEditDescription(selectedNote?.description || "");
    setIsEditing(false);
    setShowEditor(false);
    if (selectedNote) {
      supabase
        .from("scratch_pad")
        .update({ is_active: true, last_open: new Date().toISOString() })
        .eq("id", selectedNote.id)
        .eq("user_id", userId)
        .then(() => {
          supabase
            .from("scratch_pad")
            .update({ is_active: false })
            .neq("id", selectedNote.id)
            .eq("user_id", userId);
        });
    }
  }, [selectedNoteId]);

  // Use layout effect to guarantee unmount before remount ONLY when selectedNoteId changes
  useEffect(() => {
    if (!showEditor) {
      const id = setTimeout(() => setShowEditor(true), 50);
      return () => clearTimeout(id);
    }
  }, [showEditor]);

  window.addEventListener("error", (e) => {
  if (e.message === "ResizeObserver loop completed with undelivered notifications.") {
    e.stopImmediatePropagation();
  }
});

  // Add new note (draft mode)
  const handleNewNote = async () => {
    setDraftMode(true);
    setIsEditing(true);
    setEditTitle("Scratchpad"); // Always default to 'Scratchpad'
    setSelectedNoteId(null); // No note selected in list
    // Collapse sidebar when creating new note
    setShowScratchpadSidebar(false);

    // Clear description first to prevent old data showing
    setEditDescription("");

    // Clean up any existing draft first
    if (userId) {
      const draftKey = `scratchpad_new_${userId}`;
      await deleteDraft(draftKey);
    }

    // Fetch draft from Supabase (if any)
    if (userId) {
      const draftKey = `scratchpad_new_${userId}`;
      const draftValue = await getDraft(draftKey);
      if (draftValue) {
        setEditDescription(draftValue);
      }
      // If no draft, keep it empty (already set above)
    }
  };

  const fetchSessions = async () => {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();
    if (session) {
      setApiToken(session.access_token);
      // Fetch parent sessions (where parent_session is null)
      const { data: sessionList, error: parentError } = await supabase
        .from("chat_session")
        .select("*")
        .in("type", ["topic", "sub_topic"])
        .eq("user_auth", session.user.id)
        .order("updated_at", { ascending: false });

      if (parentError) {
        console.error("Error fetching parent sessions:", parentError);
      } else {
        setSideConvoList(sessionList);
      }
    }
  };

  // Helper to strip HTML tags from a string
  function stripHtml(html) {
    if (!html) return "";
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  }

  // Save note (insert if draft, update if editing existing)
  const handleSave = async () => {
    setSaving(true);
    const now = new Date();
    const nowIso = now.toISOString();
    let titleToSave = editTitle;
    if (
      draftMode &&
      (editTitle.trim() === "" || editTitle.trim().toLowerCase() === "new note")
    ) {
      // Save as 'New Note' only, no date
      titleToSave = "New Note";
    }
    // Always save only plain text (no HTML) in description
    const plainDescription = stripHtml(editDescription);
    // Save description as HTML (with formatting), not plain text
    const htmlDescription = editDescription;
    if (draftMode) {
      // Insert new note
      const { data, error } = await supabase
        .from("scratch_pad")
        .insert([
          {
            title: titleToSave,
            description: htmlDescription,
            user_id: userId,
            is_active: true,
            last_open: nowIso,
            updated_at: nowIso,
          },
        ])
        .select();
      setSaving(false);
      setIsEditing(false);
      setDraftMode(false);
      setShowScratchpadSidebar(true);
      // Delete draft after saving
      if (userId) {
        const draftKey = `scratchpad_new_${userId}`;
        await deleteDraft(draftKey);
      }
      if (!error && data && data[0]) {
        await supabase
          .from("scratch_pad")
          .update({ is_active: false })
          .neq("id", data[0].id)
          .eq("user_id", userId);
        setNotes((prev) => [
          data[0],
          ...prev.map((n) => ({ ...n, is_active: false })),
        ]);
        setSelectedNoteId(data[0].id);
      }
      return;
    }
    // Update existing note
    if (!selectedNote) return;
    const { data, error } = await supabase
      .from("scratch_pad")
      .update({
        title: editTitle,
        description: htmlDescription,
        updated_at: nowIso,
      })
      .eq("id", selectedNote.id)
      .eq("user_id", userId)
      .select();
    setSaving(false);
    setIsEditing(false);
    setShowScratchpadSidebar(true);
    if (!error && data && data[0]) {
      setNotes((prev) => {
        const updated = prev.map((n) => (n.id === selectedNote.id ? { ...n, ...data[0] } : n));
        return updated.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
      });
    }
  };

  // Cancel handler
  const handleCancel = async () => {
    if (draftMode) {
      setDraftMode(false);
      setIsEditing(false);
      setEditTitle("");
      setEditDescription("");
      setShowScratchpadSidebar(true);
      // Delete draft on cancel
      if (userId) {
        const draftKey = `scratchpad_new_${userId}`;
        await deleteDraft(draftKey);
      }
      // Optionally, select the first note in the list
      if (notes.length > 0) setSelectedNoteId(notes[0].id);
      return;
    }
    setEditTitle(selectedNote?.title || "");
    setEditDescription(selectedNote?.description || "");
    setIsEditing(false);
    setShowScratchpadSidebar(true);
  };

  // Edit handlers
  const handleChangeTitle = (e) => setEditTitle(e.target.value);
  // Save draft on every change in ReactQuill (only in draft mode)
  const handleChangeDescription = async (desc) => {
    setEditDescription(desc);
    if (draftMode && userId) {
      const draftKey = `scratchpad_new_${userId}`;
      await saveDraft(draftKey, desc);
    }
  };

  // Quill modules configuration with checklist support and image resize
  const quillModules = useMemo(() => ({
    toolbar: [
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ align: [] }],
      [{ size: ["small", false, "large", "huge"] }],
      ["link", "image"],
      ["code-block"],
      ["clean"],
    ],
    keyboard: {
      bindings: quillTabBindings,
    },
    imageResize: {
      parchment: Quill.import("parchment"),
      modules: ["Resize", "DisplaySize"],
    },
  }), []);

  // Quill formats
  const quillFormats = [
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "indent",
    "align",
    "size",
    "link",
    "image",
    "code-block",
  ];
  const handleEdit = () => {
    setIsEditing(true);
    // Collapse sidebar when entering edit mode
    setShowScratchpadSidebar(false);
  };
  const handleCreateSession = (noteId, title) => () => {
    // onHide();
    setShowQueueFormPopup(true);
    setScratchPadId(noteId);
    setScratchTitle(title);
  };

  // Filter and sort notes by search and last_open
  const filteredNotes = notes
    .filter((n) => n.title.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => new Date(b.last_open) - new Date(a.last_open));

  // Helper to format date as 'DD-MM-YYYY hh:mm AM/PM'
  function formatDateDMY(dateString) {
    if (!dateString) return "";
    const date = new Date(dateString);
    const pad = (n) => n.toString().padStart(2, "0");
    const day = pad(date.getDate());
    const month = pad(date.getMonth() + 1);
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = pad(date.getMinutes());
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 should be 12
    return `${month}-${day}-${year}  ${pad(hours)}:${minutes} ${ampm}`;
  }

  return (
    <>
      <ScratchPadSessionPopup
        show={showQueueFormPopup}
        handleClose={() => setShowQueueFormPopup(false)}
        title={"Submit Scratchpad"}
        button={"Submit"}
        sideConvoList={sideConvoList}
        apiUrl={zoomey_api_url}
        scratchPadId={scratchPadId}
        loginUserID={userId}
        apiToken={apiToken}
        scratchTitle={scratchTitle}
      />

      <Modal
        className="max_width_modal"
        show={show}
        centered
        backdrop="static"
        size="xl"
        // dialogClassName="modal-xxl"
        // style={{ height: "90vh" }}
        onHide={onHide}
      >
        <Modal.Header closeButton>
          <div className="d-flex justify-content-between align-items-center w-100">
            {isEditing || draftMode ? (
              <Form.Control
                autoFocus
                type="text"
                value={editTitle || ""}
                onChange={handleChangeTitle}
                placeholder="Note Title"
                className="note-title-input"
                style={
                  {
                    // background: "var(--textwhite)",
                    // border: "none",
                    // fontSize: 20,
                    // fontWeight: "bold",
                    // boxShadow: "none",
                    // padding: "0",
                    // width: "400px"
                  }
                }
              />
            ) : (
              <span style={{ fontWeight: "bold", fontSize: 20 }}>
                Scratchpad
              </span>
            )}
            <div className="d-flex align-items-center gap-2">
              {isEditing || draftMode ? (
                // Show Cancel & Save buttons in edit mode
                <>
                  <Button
                    style={{
                      height: 36,
                      minWidth: 80,
                      background: "var(--teal-bg)",
                      color: "var(--textwhite)",
                      border: "1px solid var(--textwhite)",
                    }}
                    onClick={handleSave}
                    disabled={saving || (!editTitle && !editDescription)}
                  >
                    {saving ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                      </>
                    ) : (
                      "Save"
                    )}
                  </Button>
                  <Button
                    variant="secondary"
                    style={{ minWidth: 80 }}
                    onClick={handleCancel}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                // Show New button in view mode
                <Button
                  className="p-2 py-1 border-0 rounded queue-create-btn"
                  onClick={handleNewNote}
                >
                  + New
                </Button>
              )}
            </div>
          </div>
        </Modal.Header>
        <Modal.Body
          className="py-0 pt-3"
          style={{ borderBottom: "1px solid var(--border-color)" }}
        >
          {/* Wave Loader while fetching scratchpads */}
          {isLoading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{
                height: "60vh",
                textAlign: "center",
              }}
            >
              <div className="loading-wave-container">
                <div className="loading-wave"></div>
                <div className="loading-wave"></div>
                <div className="loading-wave"></div>
                <div className="loading-wave"></div>
                <div className="loading-wave"></div>
              </div>
            </div>
          ) : (
          <Row style={{ height: "100%", position: "relative" }}>
            {/* Toggler button - Hide in edit mode */}
            {!(isEditing || draftMode) && (
              <div
                style={{
                  width: 0,
                  position: "absolute",
                  left: -28,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 100,
                }}
              >
                <Button
                  // variant="outline-primary"
                  style={{
                    borderRadius: "4px",
                    width: 23,
                    height: 60,
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "var(--teal-bg)",
                    border: "2px solid var(--teal-bg)",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                  }}
                  onClick={() => setShowScratchpadSidebar((prev) => !prev)}
                  aria-label={
                    showScratchpadSidebar ? "Hide sidebar" : "Show sidebar"
                  }
                >
                  {showScratchpadSidebar ? (
                    <i
                      className="bi bi-chevron-left"
                      style={{ color: "var(--whitetext)", fontSize: 22 }}
                    />
                  ) : (
                    <i
                      className="bi bi-chevron-right"
                      style={{ color: "var(--whitetext)", fontSize: 22 }}
                    />
                  )}
                </Button>
              </div>
            )}
            {/* Sidebar: Notes List */}
            <Col
              xs={4}
              md={4}
              style={{
                display: showScratchpadSidebar ? "block" : "none",
                borderRight: "1px solid var(--border-color)",
                maxHeight: "95vh",
                overflowY: "auto",
                transition: "width 0.3s",
                position: "relative",
                overflow: "visible",
              }}
            >
              {showScratchpadSidebar && (
                <>
                  <Form.Group className="mb-3">
                    <InputGroup>
                      <InputGroup.Text>
                        <i className="bi bi-search" />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Search scratchpads..."
                        value={search}
                        style={{ marginTop: 0 }}
                        onChange={(e) => setSearch(e.target.value)}
                        className={isDarkMode ? "bg-dark text-light" : ""}
                      />
                    </InputGroup>
                  </Form.Group>
                  <div
                    style={{
                      maxHeight: "80vh",
                      overflowY: "auto",
                      scrollbarWidth: "thin",
                    }}
                    className="custom-scrollbar"
                  >
                    {notes.length === 0 && !draftMode && (
                      <div
                        style={{
                          color: "#bbb",
                          textAlign: "center",
                          marginTop: 40,
                        }}
                      >
                        No notes found.{" "}
                        <span
                          style={{
                            color: "var(--text)",
                            cursor: "pointer",
                            textDecoration: "underline",
                            fontWeight: "500",
                          }}
                          onClick={handleNewNote}
                        >
                          Click here
                        </span>{" "}
                        to create one.
                      </div>
                    )}
                    {notes.length > 0 &&
                      notes
                        .filter((n) =>
                          n.title.toLowerCase().includes(search.toLowerCase())
                        )
                        .sort(
                          (a, b) =>
                            new Date(b.updated_at) - new Date(a.updated_at)
                        )
                        .map((note) => (
                          <div
                            key={note.id}
                            style={{
                              position: "relative",
                              border:
                                note.id === selectedNoteId && !draftMode
                                  ? "2px solid #b6d4fe"
                                  : ".1px solid #e5e7eb",
                              background: "var(--background)",
                              color: "var(--text)",
                              borderRadius: 10,
                              margin: "0px 10px 12px 0px",
                              padding: "14px 16px 10px 16px",
                              boxShadow:
                                note.id === selectedNoteId && !draftMode
                                  ? "0 2px 8px rgba(52, 144, 255, 0.08)"
                                  : "0 1px 4px rgba(0,0,0,0.03)",
                              cursor: "pointer",
                              transition: "all 0.15s",
                            }}
                          >
                            {/* Delete button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setNoteToDelete(note);
                                setShowDeleteModal(true);
                              }}
                              style={{
                                position: "absolute",
                                top: 8,
                                right: 8,
                                background: "transparent",
                                color: "var(--text)",
                                border: "none",
                                borderRadius: "50%",
                                width: 24,
                                height: 24,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: "bold",
                                fontSize: 18,
                                cursor: "pointer",
                                zIndex: 2,
                              }}
                              title="Delete note"
                            >
                              <i className="bi bi-x"></i>
                            </button>
                            <div
                              onClick={() => {
                                setSelectedNoteId(note.id);
                                setDraftMode(false);
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: "bold",
                                  fontSize: 16,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {note.title}
                              </div>
                              <div
                                style={{
                                  color: "#888",
                                  fontSize: 13,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {note.description &&
                                  note.description
                                    .replace(/<[^>]+>/g, "")
                                    .slice(0, 60)}
                              </div>
                              <div
                                style={{
                                  color: "#b0b0b0",
                                  fontSize: 12,
                                  marginTop: 2,
                                }}
                              >
                                {note.updated_at
                                  ? formatDateDMY(note.updated_at)
                                  : ""}
                              </div>
                            </div>
                          </div>
                        ))}
                  </div>
                </>
              )}
            </Col>
            {/* Show toggler button when sidebar is hidden */}
            {/* Remove separate toggler for hidden sidebar, handled above */}
            {/* Editor: Title + Description */}
            <Col
              xs={showScratchpadSidebar ? 8 : 12}
              md={showScratchpadSidebar ? 8 : 12}
              style={{
                maxHeight: "95vh",
                overflowY: "auto",
                padding: "0px 16px 0px 16px",
                transition: "width 0.3s",
              }}
            >
              {/* ...existing editor code... */}
              {isEditing || draftMode ? (
                <div>
                  {/* Only description editor - title is now in header */}
                  <Form.Group className="mb-3">
                    {/* Mic Button for voice input */}
                    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "8px" }}>
                      <MicButton
                        onTranscript={(text) => {
                          const currentText = typeof editDescription === "string" ? editDescription : "";
                          // Append text to existing content
                          const newText = currentText ? `${currentText}<p>${text}</p>` : `<p>${text}</p>`;
                          handleChangeDescription(newText);
                        }}
                        size="md"
                      />
                    </div>
                    {showEditor && (
                      <ReactQuill
                        key={selectedNoteId || "draft"}
                        theme="snow"
                        value={
                          typeof editDescription === "string"
                            ? editDescription
                            : ""
                        }
                        onChange={handleChangeDescription}
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="Write your notes here..."
                        style={{
                          height: "75vh",
                          marginBottom: "50px",
                        }}
                      />
                    )}
                  </Form.Group>
                </div>
              ) : selectedNote ? (
                <div style={{ position: "relative" }}>
                  {/* Header row with title and buttons */}
                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: "16px",
                    marginBottom: "4px"
                  }}>
                    <div style={{
                      fontWeight: "bold",
                      fontSize: 22,
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    }}>
                      {selectedNote?.title}
                    </div>
                    <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
                      <Button
                        variant="outline-secondary"
                        onClick={handleEdit}
                        style={{ minWidth: 90 }}
                      >
                        <i className="bi bi-pencil me-1" /> Edit
                      </Button>
                      <Button
                        variant="outline-secondary"
                        onClick={handleCreateSession(
                          selectedNote?.id,
                          selectedNote?.title
                        )}
                        style={{ minWidth: 90 }}
                      >
                        Create topic
                      </Button>
                    </div>
                  </div>
                  <p
                    style={{
                      color: "#888",
                      fontSize: "10px !important",
                      marginBottom: 8,
                    }}
                  >
                    Last updated:{" "}
                    {selectedNote?.updated_at
                      ? formatDateDMY(selectedNote.updated_at)
                      : ""}
                  </p>
                  <div
                    style={{
                      minHeight: 32,
                      color: "var(--text)",
                      fontSize: 16,
                      marginBottom: 24,
                      maxHeight: "80vh",
                      overflow: "hidden auto",
                    }}
                    className="custom-scrollbar"
                  >
                    {selectedNote?.description ? (
                      <span
                        dangerouslySetInnerHTML={{
                          __html: linkifyHtml(selectedNote.description),
                        }}
                      />
                    ) : (
                      <span style={{ color: "#bbb" }}>No description</span>
                    )}
                  </div>
                </div>
              ) : (
                <div
                  style={{ color: "#bbb", textAlign: "center", marginTop: 80 }}
                >
                  No notes found
                </div>
              )}
            </Col>
          </Row>
          )}
        </Modal.Body>
        {/* Confirmation Modal for Delete */}
        <ConfirmationPopup
          show={showDeleteModal}
          handleClose={() => {
            setShowDeleteModal(false);
            setNoteToDelete(null);
          }}
          title="Delete Note"
          body={null}
          message={
            noteToDelete
              ? `Are you sure you want to delete the note "${noteToDelete.title}"?`
              : ""
          }
          handleConfirm={async () => {
            if (!noteToDelete) return;
            await supabase
              .from("scratch_pad")
              .delete()
              .eq("id", noteToDelete.id)
              .eq("user_id", userId);
            setNotes((prev) => prev.filter((n) => n.id !== noteToDelete.id));
            if (selectedNoteId === noteToDelete.id) {
              setSelectedNoteId(
                notes.filter((n) => n.id !== noteToDelete.id)[0]?.id || null
              );
            }
            setShowDeleteModal(false);
            setNoteToDelete(null);
          }}
        />
      </Modal>
    </>
  );
}

export default NotesModal;
